import { eff } from "@/core/utils/effect-ts-abbr";
import { useI18n } from "@/shared/composables/use-i18n";
import { watch } from "@vue/runtime-core";
import { setup, Vue } from "vue-class-component";
import { Model, Prop } from "vue-property-decorator";
import "./app-pagination.scss";
import { useWindow } from "@/shared/composables/use-window";

export enum AppPaginationPerPage {
    _12 = 12,
    _24 = 24,
    _36 = 36,
    _48 = 48,
}

export class AppPagination extends Vue {
    i18n = setup(useI18n);
    windowCtx = setup(useWindow);
    isTransparent = false;

    @Model("modelValue", { required: true, type: Number })
    currentPage!: number;

    @Prop({ required: true, type: Number })
    total!: number;

    @Model("perPage", { required: true, type: Number })
    perPageModel!: AppPaginationPerPage;

    @Prop({ type: Function })
    handlePageChange?: (page: number) => void;

    get perPageOptions() {
        return eff.fn.pipe(
            [
                AppPaginationPerPage._12,
                AppPaginationPerPage._24,
                AppPaginationPerPage._36,
                AppPaginationPerPage._48,
                null,
            ],
            eff.array.map((perPage) => ({
                label: this.getPerPageLabel(perPage),
                value: perPage,
            })),
        );
    }

    get totalPages() {
        return Math.ceil(this.total / this.perPageModel);
    }

    get fewItemsBeforeCurrentPage(): readonly number[] {
        return eff.fn.pipe(
            eff.array.range(1, this.currentPage - 1),
            (it) => it.slice(-2),
            eff.array.filter((n) => n !== this.currentPage),
        );
    }

    get fewItemsAfterCurrentPage(): readonly number[] {
        if (this.currentPage >= this.totalPages) {
            return [];
        }

        return eff.fn.pipe(eff.array.range(this.currentPage + 1, this.totalPages), (it) =>
            it.slice(0, 2),
        );
    }

    get hasPreviousPage() {
        return this.currentPage > 1;
    }

    get hasNextPage() {
        return this.totalPages - this.currentPage >= 1;
    }

    get canShowFirstPage() {
        return this.currentPage > 3;
    }

    get canShowLastPage() {
        return this.totalPages - this.currentPage >= 3;
    }

    get hasHiddenPagesBeforeCurrentPage() {
        return this.currentPage > 4;
    }

    get hasHiddenPagesAfterCurrentPage() {
        return this.totalPages - this.currentPage > 3;
    }

    getPerPageLabel(perPage: AppPaginationPerPage | null) {
        if (perPage === null) {
            return this.i18n.t("common.all");
        }

        return perPage.toString();
    }

    goToPage(page: number) {
        this.currentPage = page;
        this.handlePageChange?.(page);
    }

    goToNextPage() {
        this.currentPage++;
        this.handlePageChange?.(this.currentPage);
    }

    goToPreviousPage() {
        this.currentPage--;
        this.handlePageChange?.(this.currentPage);
    }

    goToFirstPage() {
        this.currentPage = 1;
        this.handlePageChange?.(this.currentPage);
    }

    goToLastPage() {
        this.currentPage = this.totalPages;
        this.handlePageChange?.(this.totalPages);
    }

    mounted() {
        watch(
            () => ({
                w: this.windowCtx.w,
            }),
            (w) => {
                const x = document
                    .getElementById("c-app-pagination")
                    ?.getClientRects()[0];
                if (!x) return;
                this.isTransparent = x.y + x.height < window.innerHeight - 1;
            },
            { immediate: true },
        );

        watch(
            () => ({
                perPage: this.perPageModel,
            }),
            () => {
                if (this.currentPage !== 1) {
                    this.currentPage = 1;
                }
            },
        );

        watch(
            () => ({
                perPage: this.perPageModel,
                currentPage: this.currentPage,
            }),
            () => {
                document.documentElement.scrollTop = 0;
            },
        );
    }
}
